import { FC } from 'react';
import styled from 'styled-components';
import { Icon } from '../Icon/Icon';

const StyledHealthMetric = styled.div<{ positive: boolean; neutral: boolean }>`
  align-items: center;
  color: ${({ neutral, positive, theme }) =>
    positive ? theme.vars.newColor.green2 : neutral ? theme.vars.newColor.darkGS2 : theme.vars.newColor.red2};
  display: flex;
  flex-wrap: nowrap;
  font-size: 16px;
  font-weight: 600;
  line-height: 19px;
`;

const HealthMetricArrow = styled.div`
  align-items: center;
  display: inline-flex;
  font-size: 14px;
  margin-right: 10px;
`;

const HealthMetricText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface IHealthMetricProps {
  /** New value. */
  newValue: number;
  /** Old value. */
  oldValue: number;
  /** Additional descriptive text. */
  text?: string;
  /** Show the arrow icon. (Defaults to true) */
  showArrow?: boolean;
  /** Show the negative sign next to the number. (Defaults to false) */
  showSign?: boolean;
}

export const HealthMetric: FC<IHealthMetricProps> = ({
  newValue,
  oldValue,
  text,
  showArrow = true,
  showSign = false,
}) => {
  if (typeof oldValue === 'undefined' || typeof newValue === 'undefined') {
    return null;
  }

  const change = getPercentageChange(oldValue, newValue);
  const positive = change > 0;
  const neutral = change === 0;
  const icon = positive ? 'arrow-circle-up' : neutral ? 'minus-circle' : 'arrow-circle-down';

  return (
    <StyledHealthMetric data-testid="HealthMetric" positive={positive} neutral={neutral}>
      {showArrow && (
        <HealthMetricArrow data-testid="HealthMetricArrow">
          <Icon icon={icon} />
        </HealthMetricArrow>
      )}

      <HealthMetricText data-testid="HealthMetricText">
        {formatMetric(showSign ? change : Math.abs(change))} {text}
      </HealthMetricText>
    </StyledHealthMetric>
  );
};

const minimumNonZeroValue = 0.1;
export function formatMetric(metric: number): string {
  if (!metric || Math.abs(metric) < minimumNonZeroValue) {
    return '0%';
  }

  return `${Math.round(metric * 10) / 10}%`;
}

export function getPercentageChange(originalValue: number, newValue: number, removeSign = false) {
  if (originalValue === newValue) {
    return 0;
  }

  const change = ((newValue - originalValue) / originalValue) * 100;

  if (!removeSign) {
    return change;
  }

  return Math.abs(change);
}

export default HealthMetric;
